import { Stack, Typography } from '@mui/material';
import { useDealer } from '../../hooks/useDealer';
import { useDiscountPrice } from '../../store/discountContext';

const formatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

const applyDiscountPercent = (price: number, discountPercent: number) => {
  const discountValue = price * (discountPercent / 100);
  return price - discountValue;
};

const applyDiscountPercents = (price: number, discountPercents: number[]) => {
  let discountedPrice = price;
  discountPercents.forEach((discountPercent) => {
    discountedPrice =
      discountedPrice - discountedPrice * (discountPercent / 100);
  });
  return discountedPrice;
};

const PriceLine = ({
  price,
  suffix = '',
  gutterBottom = false,
  type = 'primary',
}: {
  price: number;
  suffix: string;
  type: 'primary' | 'primary-default' | 'secondary' | 'tertiary';
  gutterBottom?: boolean;
}) => {
  let fontColor;
  switch (type) {
    case 'primary':
      fontColor = '#E2001A';
      break;
    case 'tertiary':
      fontColor = '#959798';
      break;
    case 'primary-default':
      fontColor = '#373737';
      break;
    default:
      fontColor = '#373737';
      break;
  }

  const isPrimary =
    type === 'primary' || type === 'primary-default' || type === 'secondary';

  return (
    <div>
      <Typography
        variant={isPrimary ? 'h5' : 'h6'}
        sx={{
          color: fontColor,
          display: 'inline-block',
          fontWeight: isPrimary ? '600' : undefined,
          fontSize: isPrimary ? undefined : '0.875rem',
        }}
      >
        {`${formatter.format(price)}`}
      </Typography>
      <Typography
        variant={'h6'}
        sx={{
          color: fontColor,
          fontSize: '0.875rem',
          display: 'inline-block',
          marginLeft: '0.3rem',
        }}
        gutterBottom={gutterBottom}
      >
        {`${suffix}`}
      </Typography>
    </div>
  );
};

const ShippingLabel = () => {
  const dealer = useDealer();
  const fontColor = '#373737';
  const fontSize = '0.875rem';

  if (dealer === 'schrankwerkde') {
    return (
      <Typography sx={{ fontSize: fontSize, color: fontColor }}>
        Inkl. MwSt., Lieferung in 8-9 Wochen
      </Typography>
    );
  }

  return (
    <Typography sx={{ fontSize: fontSize, color: fontColor }}>
      Inkl. MwSt., Lieferung und Montage in 8-9 Wochen
    </Typography>
  );
};

export function PriceInformation() {
  const discountInfo = useDiscountPrice();

  if (!discountInfo) {
    return null;
  }

  const aDiscount = discountInfo.aDiscount;
  const cDiscount = discountInfo.cDiscount;

  const cPercent = cDiscount?.percentage ?? 0;
  const aPercent = aDiscount?.percentage ?? 0;

  const isCDiscountGreaterThanZero = (cDiscount?.percentage ?? 0) > 0;
  const isADiscountGreaterThanZero = (aDiscount?.percentage ?? 0) > 0;

  const areCAndAGreaterThanZero =
    isCDiscountGreaterThanZero && isADiscountGreaterThanZero;

  const isCGreaterThanZeroAndAZero =
    isCDiscountGreaterThanZero && !isADiscountGreaterThanZero;

  const isCZeroAndAGreaterThanZero =
    !isCDiscountGreaterThanZero && isADiscountGreaterThanZero;

  const areCAndAZero =
    !isCDiscountGreaterThanZero && !isADiscountGreaterThanZero;

  return (
    <Stack sx={{ p: 3 }}>
      {areCAndAGreaterThanZero && (
        <>
          <PriceLine
            price={discountInfo.fullPrice}
            suffix={'ohne Nachlässe'}
            type="tertiary"
          />
          <PriceLine
            price={applyDiscountPercent(discountInfo.fullPrice, cPercent)}
            suffix={`mit ${cPercent}% Nachlass`}
            type="secondary"
          />
          <PriceLine
            price={applyDiscountPercents(discountInfo.fullPrice, [
              cPercent,
              aPercent,
            ])}
            suffix={'mit Kundenkarten-Aktion'}
            type="primary"
          />
          <ShippingLabel />
        </>
      )}

      {isCGreaterThanZeroAndAZero && (
        <>
          <PriceLine
            price={discountInfo.fullPrice}
            suffix={'ohne Nachlässe'}
            type="tertiary"
          />
          <PriceLine
            price={applyDiscountPercent(discountInfo.fullPrice, cPercent)}
            suffix={`mit ${cPercent}% Nachlass`}
            type="primary"
          />
          <ShippingLabel />
        </>
      )}

      {isCZeroAndAGreaterThanZero && (
        <>
          <PriceLine
            price={discountInfo.fullPrice}
            suffix={'ohne Nachlässe'}
            type="tertiary"
          />
          <PriceLine
            price={applyDiscountPercent(discountInfo.fullPrice, aPercent ?? 0)}
            suffix={'mit Kundenkarten-Aktion'}
            type="primary"
          />
          <ShippingLabel />
        </>
      )}

      {areCAndAZero && (
        <>
          <PriceLine
            price={discountInfo.fullPrice}
            suffix={''}
            type="primary-default"
          />
          <ShippingLabel />
        </>
      )}
    </Stack>
  );
}
